import { NestedFieldPaths } from 'ag-grid-community';
import { AxiosRequestConfig } from 'axios';
import { useAtomValue } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { set } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import { FieldPath, useFormContext, useFormState, useWatch } from 'react-hook-form';
import * as yup from 'yup';
import { companyWebsiteField } from '../../../data-fields/CommonFields';
import { ICompanyDataModel } from '../../../data-models/company.data-model';
import { RendererType } from '../../../data-models/field-definition';
import {
  field2ToFormField,
  FieldEntity,
  FormOptions,
  IField,
  ISimpleChoice,
} from '../../../data-models/field2.data-model';
import { companyNameSearchField } from '../../../schemas/common-schema-defs';
import { CompanyDataModel2, companyFields } from '../../../schemas/Company.schema';
import { useCreateCompanyAndUpdateState } from '../../../services/hooks/useCreateCompanyAndUpdateState';
import { useUpdateCompanyAndState } from '../../../services/hooks/useUpdateCompanyAndState';
import { fetchCompaniesByWebsite } from '../../../services/queries/MaggieCompanyQueries';
import { customFieldsByEntityAtom } from '../../../services/state/AdminPanel/CustomFieldsStateJ';
import { sectorsAtom, usersAtom } from '../../../services/state/AppConfigStateJ';
import { companyStateJ } from '../../../services/state/CompanyStateJ';
import { FDMap } from '../../../util/data-structure/FDMap';
import { getForesightStore } from '../../../util/jotai-store';
import { VALID_URL_REGEX } from '../../../util/regex';
import { schemaToFormFields } from '../../../util/schema-utils';
import { ICompanyViewModel } from '../../../view-models/company.view-model';
import { FormVariant, IFormField, IFormFieldSelectMeta } from '../../../view-models/form.view-model';

export function useCreateOrUpdateCompanyHandler(
  createConfig?: AxiosRequestConfig<Partial<ICompanyDataModel>>
) {
  const createCompany = useCreateCompanyAndUpdateState(true, createConfig);
  const updateCompany = useUpdateCompanyAndState();

  return useAtomCallback(
    useCallback(
      async (get, set, data: Partial<ICompanyDataModel>) => {
        const modifiedCustomData = removeEmptyCustomData(data.customData ?? {});
        const payload = { ...pickFormFields(data), id: data.id, customData: modifiedCustomData };
        let company;
        if (!payload.id) {
          company = await createCompany(payload);
        } else {
          company = await updateCompany(payload.id!, payload);
        }
        if (company) {
          set(companyStateJ(company.id), company);
        }
        return company;
      },
      [createCompany, updateCompany]
    )
  );
}

export const companyWebsiteSchema = yup
  .string()
  .nullable()
  .test('is-url', 'Invalid URL', (value) => {
    if (!value) {
      return true;
    }
    return value.match(VALID_URL_REGEX) !== null;
  })
  .test(checkDuplicateWebsite);

// avoid sending entire company object on every put request
export function pickFormFields(data: Partial<ICompanyDataModel>) {
  const { ceoName, primaryLocation, secondaryLocations, customData } = data;
  const rest = Object.keys(companyFormFields()) as (keyof ICompanyDataModel)[];
  return {
    ceoName,
    primaryLocation,
    secondaryLocations,
    customData,
    ...rest.reduce((acc, path) => {
      if (path.startsWith('_viewModel')) {
        return acc;
      }
      set(acc, path, data[path]);
      return acc;
    }, {} as Partial<ICompanyDataModel>),
  };
}

export function companyFormFields() {
  const store = getForesightStore();
  const userOptions = store.get(usersAtom).map((u) => ({
    value: u.id,
    displayName: u.name,
  }));
  const sectorOptions = store.get(sectorsAtom).map((s) => ({
    value: s.id,
    displayName: s.name,
  }));
  const { dealLeadId, dealTeamIds, name, shortDescription } = companyFields();
  return {
    name: name.required(),
    shortDescription,
    socials: yup.mixed(),
    dealLeadId: dealLeadId.formMeta({
      formatter: 'user',
      renderer: RendererType.singleSelect,
      rendererMeta: {
        values: userOptions,
      },
    }),
    dealTeamIds: dealTeamIds.formMeta({
      formatter: 'user',
      renderer: RendererType.multiSelect,
      rendererMeta: {
        values: userOptions,
      },
    }),
    sectorId: yup
      .number()
      .nullable()
      .label('Sector')
      .default(null)
      .formMeta<IFormFieldSelectMeta<number>>({
        key: 'sectorId',
        label: 'Sector',
        formatter: 'sector',
        renderer: RendererType.singleSelect,
        rendererMeta: { values: sectorOptions },
      }),
    sourceCompany: companyNameSearchField().label('Source Company'),
    _viewModel: yup.object({
      ...companyViewModelFields(),
    }),
    website: companyWebsiteSchema,
  };
}

export function companyViewModelFields() {
  return {
    ceoName: yup
      .array()
      .of(yup.string().required())
      .formMeta<IFormFieldSelectMeta<string>>({
        key: '_viewModel.ceoName',
        label: 'CEO Names',
        formatter: 'stringArray',
        renderer: RendererType.multiSelect,
        rendererMeta: {
          allowCustomAdd: true,
          values: [], // TODO
        },
      }),
    founderNames: yup
      .array()
      .of(yup.string())
      .nullable()
      .label('Founders')
      .formMeta<IFormFieldSelectMeta<string>>({
        key: '_viewModel.founderNames',
        label: 'Founders',
        formatter: 'stringArray',
        renderer: RendererType.multiSelect,
        rendererMeta: {
          allowCustomAdd: true,
          values: [], // needs to be set dynamically based on company data
        },
      }),
    lastFunding: yup.string().label('Last Funding').formMeta({
      key: '_viewModel.lastFunding',
      disabled: true,
    }),
    primaryLocation: yup.mixed().nullable().label('Location').default(null), // we cannot set formMeta on yup.mixed(), and if we set primaryLocation to yup.string(), it will trigger a type validation error
    secondaryLocations: yup.array().label('Secondary Locations').default([]),
    totalRaised: yup.number().label('Total Raised').customMeta({
      formatter: 'currencyShort',
    }),
  };
}

export function companyFormSchema(): yup.ObjectSchema<Partial<ICompanyViewModel>> {
  return yup.object({
    ...companyFormFields(),
  }) as yup.ObjectSchema<Partial<ICompanyViewModel>>;
}

export const defaultCompanyFormFields: FieldPath<ICompanyViewModel>[] = [
  'name',
  'website',
  '_viewModel.primaryLocation',
  '_viewModel.secondaryLocations',
  '_viewModel.ceoName',
  'sectorId',
  'shortDescription',
  'dealLeadId',
  'dealTeamIds',
];
interface ICompanyFieldsProps {
  fields?: (keyof CompanyDataModel2 | FieldPath<ICompanyViewModel>)[];
  variant?: FormVariant;
  customFields?: 'none' | 'all' | Set<FormOptions>;
}

export function useCompanyFields({
  fields = defaultCompanyFormFields,
  variant = 'form',
  customFields = 'all',
}: ICompanyFieldsProps = {}) {
  const formFieldMap = useMemo(() => {
    // @ts-expect-error - FIXME: https://foresightdata.atlassian.net/browse/MAGGIE-9420
    const formFields = schemaToFormFields(
      yup.object({
        ...companyFields(),
        ...companyFormFields(),
      }),
      fields as NestedFieldPaths<CompanyDataModel2>[]
    );

    return FDMap.fromArray(formFields, 'key');
  }, [fields]);

  const fieldsOrderMap = useMemo(() => {
    const result = new Map<keyof CompanyDataModel2 | FieldPath<ICompanyViewModel>, number>();
    fields.forEach((field, index) => {
      result.set(field, index);
    });
    return result;
  }, [fields]);

  const customFieldsOptions = customFields instanceof Set ? { includeFormOptions: customFields } : undefined;
  const allCustomFields = useCompanyCustomFields(customFieldsOptions);

  const dynamicFields = useDynamicCompanyFields({
    baseFormFieldMap: formFieldMap as Map<
      keyof CompanyDataModel2 | FieldPath<ICompanyViewModel>,
      IFormField<unknown>
    >,
    formVariant: variant,
  });

  return useMemo(() => {
    const result: IFormField<unknown>[] = [];
    Object.values(dynamicFields).forEach((field) => {
      if (field) {
        result.push(field);
      }
    });
    const primaryLocation = formFieldMap.get('_viewModel.primaryLocation');
    // we cannot set formMeta for location in the schema (see comment in companyViewModelFields)
    if (primaryLocation) {
      result.push({
        ...primaryLocation,
        renderer: RendererType.location,
        rendererMeta: {
          multi: false,
          values: [],
        },
      });
    }
    const secondaryLocations = formFieldMap.get('_viewModel.secondaryLocations');
    if (secondaryLocations) {
      result.push({
        ...secondaryLocations,
        renderer: RendererType.location,
        rendererMeta: {
          multi: true,
          values: [],
        },
      });
    }

    const fieldsWithCustomSettings = new Set<FieldPath<ICompanyViewModel>>([
      'latestInvestors',
      'latestLeadInvestor',
      'name',
      'website',
      '_viewModel.ceoName',
      '_viewModel.founderNames',
      '_viewModel.primaryLocation',
      '_viewModel.secondaryLocations',
    ]);

    result.push(
      ...[...formFieldMap.values()].filter(
        (field) => !fieldsWithCustomSettings.has(field.key as keyof ICompanyViewModel)
      )
    );
    result.sort(
      (a, b) =>
        (fieldsOrderMap.get(a.key as (typeof fields)[0]) ?? Number.MAX_SAFE_INTEGER) -
        (fieldsOrderMap.get(b.key as (typeof fields)[0]) ?? Number.MAX_SAFE_INTEGER)
    );
    if (customFields !== 'none') {
      result.push(...allCustomFields);
    }
    return result;
  }, [allCustomFields, customFields, dynamicFields, fieldsOrderMap, formFieldMap]);
}

function useDynamicCompanyFields({
  baseFormFieldMap,
  formVariant = 'form',
}: {
  baseFormFieldMap: Map<keyof CompanyDataModel2 | FieldPath<ICompanyViewModel>, IFormField<unknown>>;
  formVariant: FormVariant;
}) {
  const { control } = useFormContext<Partial<ICompanyViewModel>>();
  const { defaultValues } = useFormState({ control });
  const companyData = defaultValues ?? {};
  const ceoNames = useWatch({ name: '_viewModel.ceoName', control });
  const latestLeadInvestors = useWatch({ name: 'latestLeadInvestor', control });
  const latestLeadInvestorsOptions: ISimpleChoice<string>[] = useMemo(() => {
    return (
      latestLeadInvestors?.map((name) => {
        return { displayName: name, value: name ?? '' };
      }) ?? []
    );
  }, [latestLeadInvestors]);
  const latestInvestors = useWatch({ name: 'latestInvestors', control });
  const latestInvestorsOptions: ISimpleChoice<string>[] = useMemo(() => {
    return (
      latestInvestors?.map((name) => {
        return { displayName: name, value: name ?? '' };
      }) ?? []
    );
  }, [latestInvestors]);
  const isCrunchbase = companyData?.id != null && Boolean(companyData?.crunchbaseId);
  const ceoNameOptions: ISimpleChoice<string>[] = useMemo(() => {
    return (
      ceoNames?.map((name) => {
        return { displayName: name, value: name ?? '' };
      }) ?? []
    );
  }, [ceoNames]);

  const founderNames = useWatch({ name: '_viewModel.founderNames', control });
  const founderNameOptions: ISimpleChoice<string>[] = useMemo(() => {
    return (
      founderNames?.map((name) => {
        return { displayName: name, value: name ?? '' };
      }) ?? []
    );
  }, [founderNames]);

  return useMemo(() => {
    const result = {
      ceoNameField: baseFormFieldMap.get('_viewModel.ceoName'),
      foundersField: baseFormFieldMap.get('_viewModel.founderNames'),
      latestLeadInvestorsField: baseFormFieldMap.get('latestLeadInvestor'),
      latestInvestorsField: baseFormFieldMap.get('latestInvestors'),
      nameField: baseFormFieldMap.get('name'),
      websiteField: baseFormFieldMap.get('website'),
    };

    if (result.nameField) {
      result.nameField.disabled = isCrunchbase;
    }

    if (result.websiteField) {
      if (formVariant == 'form') {
        result.websiteField = companyWebsiteField({
          key: 'website',
          disabled: isCrunchbase,
        });
      } else {
        result.websiteField = {
          ...schemaToFormFields(yup.object(companyFields()), ['website'])[0],
          disabled: isCrunchbase,
        };
      }
    }

    if (result.ceoNameField) {
      result.ceoNameField.rendererMeta = {
        allowCustomAdd: true,
        values: ceoNameOptions,
      };
    }

    if (result.latestLeadInvestorsField) {
      result.latestLeadInvestorsField.rendererMeta = {
        allowCustomAdd: true,
        values: latestLeadInvestorsOptions,
      };
    }

    if (result.latestInvestorsField) {
      result.latestInvestorsField.rendererMeta = {
        allowCustomAdd: true,
        values: latestInvestorsOptions,
      };
    }

    if (result.foundersField) {
      result.foundersField.disabled = true; // TODO: remove when BE is ready
      result.foundersField.rendererMeta = {
        allowCustomAdd: true,
        values: founderNameOptions,
      };
    }

    return result;
  }, [
    baseFormFieldMap,
    ceoNameOptions,
    formVariant,
    founderNameOptions,
    isCrunchbase,
    latestInvestorsOptions,
    latestLeadInvestorsOptions,
  ]);
}

export function useCompanyCustomFields(options?: {
  includeFormOptions?: Set<FormOptions>;
  paths?: string[];
}) {
  const customFields = useAtomValue(customFieldsByEntityAtom);
  return (customFields.get(FieldEntity.company) ?? []).reduce((acc, field) => {
    if (shouldIncludeFormOption(field, options)) {
      acc.push(field2ToFormField(field));
    }
    return acc;
  }, [] as IFormField<unknown>[]);
}

export const DuplicateWebsiteErrorType = 'duplicate';

export async function checkDuplicateWebsite(
  value: string | null | undefined,
  context: yup.TestContext<yup.AnyObject>
) {
  if (!value || !VALID_URL_REGEX.test(value)) {
    return true;
  }
  const companies = await fetchCompaniesByWebsite(value);

  const company = companies?.at(0);

  if (!company || company.id === context.parent?.id) {
    return true;
  } else {
    return context.createError({
      path: `website`,
      message: 'Duplicate website',
      type: DuplicateWebsiteErrorType,
      params: {
        company: { ...company },
      },
    });
  }
}

// react hook form creates undefined values for empty custom data fields - to avoid api calls when no data changed, remove them from payload
// (update handler will check if data changed before sending request)
export function removeEmptyCustomData(formData: ICompanyDataModel['customData']) {
  if (!formData) {
    return formData;
  }
  return Object.entries(formData).reduce(
    (acc, [key, value]) => {
      if (value !== undefined) {
        acc![key] = value;
      }
      return acc;
    },
    {} as ICompanyDataModel['customData']
  );
}

export function shouldIncludeFormOption(
  field: IField<unknown>,
  options?: { includeFormOptions?: Set<FormOptions>; paths?: string[] }
) {
  if (options?.paths && !options.paths.includes(field.entityField)) {
    return false;
  }

  if (
    options?.includeFormOptions &&
    options.includeFormOptions.intersection(new Set(field.systemMeta?.formRefs ?? [])).size === 0
  ) {
    return false;
  }

  return true;
}
